import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { UnifiedCustomerSelector } from "./UnifiedCustomerSelector";
import {
  areCustomersEqual,
  processCustomerSelections,
  sortCustomerOptions,
  transformCustomerListItem,
} from "./utils";
import { useSupplierCustomers } from "../../../../../../hooks";

import type {
  CustomerSelection,
  CustomerSelectionItem,
  UnifiedCustomerSelectorContainerProps,
} from "./types";

function UnifiedCustomerSelectorContainer({
  onCustomerSelectionChange,
  initialCustomerRoleIds = [],
  initialExternalCustomerIds = [],
  initialCustomerInviteEmails = [],
  initialCustomerSnapshots = [],
  excludeCustomerRoleIds = [],
  excludeExternalCustomerIds = [],
  excludeCustomerEmails = [],
  disabled = false,
  isInAddCustomersToShipmentModal = false,
  hideBorder = false,
  autoFocus = false,
}: UnifiedCustomerSelectorContainerProps) {
  // State for current selections and input
  const [selectedItems, setSelectedItems] = useState<CustomerSelectionItem[]>(
    [],
  );
  const [inputValue, setInputValue] = useState<string>("");

  // Track previous initial props to detect template changes
  const prevInitialPropsRef = useRef({
    customerRoleIds: initialCustomerRoleIds,
    externalCustomerIds: initialExternalCustomerIds,
    customerInviteEmails: initialCustomerInviteEmails,
    customerSnapshots: initialCustomerSnapshots,
  });
  const hasInitializedRef = useRef(false);

  // Fetch all customers using the unified hook
  const { data: customersData, loading, error } = useSupplierCustomers();

  // Transform customer data with error handling
  const { allCustomers, transformationError } = useMemo(() => {
    try {
      const transformedCustomers = customersData.map(transformCustomerListItem);

      // Filter out excluded customers
      const filteredCustomers = transformedCustomers.filter((customer) => {
        // Check for excluded internal customers
        if (customer.type === "internal_customer") {
          return !excludeCustomerRoleIds.includes(customer.role_id);
        }

        // Check for excluded external customers
        if (customer.type === "external_customer") {
          return !excludeExternalCustomerIds.includes(customer.id);
        }

        // Check for excluded emails (case-insensitive)
        const normalizedExcludeEmails = excludeCustomerEmails.map((email) =>
          email.toLowerCase(),
        );
        return !normalizedExcludeEmails.includes(customer.email.toLowerCase());
      });

      const sortedCustomers = sortCustomerOptions(filteredCustomers);
      return { allCustomers: sortedCustomers, transformationError: null };
    } catch {
      return {
        allCustomers: [],
        transformationError:
          "Unable to load your customer list right now. Please refresh the page or try again in a few moments.",
      };
    }
  }, [
    customersData,
    excludeCustomerRoleIds,
    excludeExternalCustomerIds,
    excludeCustomerEmails,
  ]);

  // Combine API error and transformation error
  const finalError = error ?? transformationError;
  // Check if user truly has no customers (vs all customers being filtered out)
  const hasNoCustomers =
    !loading && customersData.length === 0 && !transformationError;

  /**
   * Initialize selected items from props (for copy/template scenarios)
   */
  useEffect(() => {
    const prevProps = prevInitialPropsRef.current;

    // Check if initial props have changed (indicates new template being applied)
    const propsChanged =
      JSON.stringify(prevProps.customerRoleIds) !==
        JSON.stringify(initialCustomerRoleIds) ||
      JSON.stringify(prevProps.externalCustomerIds) !==
        JSON.stringify(initialExternalCustomerIds) ||
      JSON.stringify(prevProps.customerInviteEmails) !==
        JSON.stringify(initialCustomerInviteEmails) ||
      JSON.stringify(prevProps.customerSnapshots) !==
        JSON.stringify(initialCustomerSnapshots);

    // Only initialize if:
    // 1. We haven't initialized yet AND data is loaded (initial load)
    // 2. OR template props have changed (new template applied)
    const shouldInitialize =
      (!hasInitializedRef.current && !loading) || (propsChanged && !loading);

    if (!shouldInitialize) {
      return;
    }

    const initialItems: CustomerSelectionItem[] = [];

    // Add internal customers, falling back to the template's own snapshot of
    // this customer's identity when the id no longer resolves live (e.g.
    // removed from the network since the template was saved -- see #2836)
    initialCustomerRoleIds.forEach((roleId) => {
      const customer = allCustomers.find(
        (c) => c.type === "internal_customer" && c.role_id === roleId,
      );
      if (customer) {
        initialItems.push(customer);
        return;
      }

      const snapshot = initialCustomerSnapshots.find(
        (
          s,
        ): s is Extract<
          TemplateCustomerSnapshot,
          { type: "internal_customer" }
        > => s.type === "internal_customer" && s.role_id === roleId,
      );
      if (snapshot) {
        initialItems.push({
          type: "internal_customer",
          id: snapshot.role_id,
          role_id: snapshot.role_id,
          email: snapshot.email,
          first_name: snapshot.first_name,
          last_name: snapshot.last_name,
          company_name: snapshot.company_name,
          isRemovedFromNetwork: true,
        });
      }
    });

    // Add external customers, with the same snapshot fallback
    initialExternalCustomerIds.forEach((customerId) => {
      const customer = allCustomers.find(
        (c) => c.type === "external_customer" && c.id === customerId,
      );
      if (customer) {
        initialItems.push(customer);
        return;
      }

      const snapshot = initialCustomerSnapshots.find(
        (
          s,
        ): s is Extract<
          TemplateCustomerSnapshot,
          { type: "external_customer" }
        > => s.type === "external_customer" && s.id === customerId,
      );
      if (snapshot) {
        initialItems.push({
          type: "external_customer",
          id: snapshot.id,
          email: snapshot.email,
          first_name: snapshot.first_name,
          last_name: snapshot.last_name,
          company_name: snapshot.company_name,
          isRemovedFromNetwork: true,
        });
      }
    });

    // Add email invites, but normalize them first
    // Check if any emails now correspond to existing customers (from templates)
    const normalizedEmails: string[] = [];
    initialCustomerInviteEmails.forEach((email) => {
      // Try to find existing customer with this email
      const existingCustomer = allCustomers.find(
        (c) => c.email.toLowerCase() === email.toLowerCase(),
      );

      if (existingCustomer) {
        // Email now corresponds to existing customer, add as customer instead
        if (
          !initialItems.some((item) =>
            areCustomersEqual(item, existingCustomer),
          )
        ) {
          initialItems.push(existingCustomer);
        }
      } else {
        // Still an email invite
        normalizedEmails.push(email);
      }
    });

    // Add remaining email invites that don't correspond to existing customers
    normalizedEmails.forEach((email) => {
      initialItems.push({
        type: "email",
        email,
      });
    });

    setSelectedItems(initialItems);

    // Template data normalization: If any emails were converted to existing customers,
    // we need to update the parent form state to reflect the normalized data.
    // This prevents the form from trying to create duplicate invites when submitted.
    //
    // Example: Template has customerInviteEmails: ['john@company.com']
    // But John is now an existing customer with role_id: 123
    // We need to update parent to: customerRoleIds: [123], customerInviteEmails: []
    if (normalizedEmails.length < initialCustomerInviteEmails.length) {
      const normalizedResult = processCustomerSelections(initialItems);
      onCustomerSelectionChange(normalizedResult);
    }

    // Update previous props reference after processing template changes
    prevInitialPropsRef.current = {
      customerRoleIds: initialCustomerRoleIds,
      externalCustomerIds: initialExternalCustomerIds,
      customerInviteEmails: initialCustomerInviteEmails,
      customerSnapshots: initialCustomerSnapshots,
    };
    hasInitializedRef.current = true;
  }, [
    allCustomers,
    loading,
    initialCustomerRoleIds,
    initialExternalCustomerIds,
    initialCustomerInviteEmails,
    initialCustomerSnapshots,
    onCustomerSelectionChange,
  ]);

  /**
   * Handles selection changes from the presentation component
   */
  const handleSelectionChange = useCallback(
    (newSelections: CustomerSelection[]) => {
      // Convert strings to customer items and update state
      const processedSelections: CustomerSelectionItem[] = newSelections.map(
        (item) => {
          if (typeof item === "string") {
            return { type: "email", email: item.trim() };
          }
          return item;
        },
      );

      setSelectedItems(processedSelections);

      // Process selections and notify parent
      const result = processCustomerSelections(newSelections);
      onCustomerSelectionChange(result);
    },
    [onCustomerSelectionChange],
  );

  /**
   * Handles input value changes
   */
  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
  }, []);

  /**
   * Filter options to exclude already selected items
   */
  const availableOptions = useMemo(
    (): CustomerSelectionItem[] =>
      allCustomers.filter(
        (customer) =>
          !selectedItems.some((selected) =>
            areCustomersEqual(customer, selected),
          ),
      ),
    [allCustomers, selectedItems],
  );

  return (
    <UnifiedCustomerSelector
      selectedItems={selectedItems}
      options={availableOptions}
      inputValue={inputValue}
      onSelectionChange={handleSelectionChange}
      onInputChange={handleInputChange}
      loading={loading}
      error={finalError}
      hasNoCustomers={hasNoCustomers}
      disabled={disabled}
      isInAddCustomersToShipmentModal={isInAddCustomersToShipmentModal}
      hideBorder={hideBorder}
      autoFocus={autoFocus}
    />
  );
}

export { UnifiedCustomerSelectorContainer };
